import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Info } from 'lucide-react';
import api from 'config/axiosConfig';
import { apiRoutes } from "util/apiRoutes";
import InputField from 'ui/InputField';
import Modal from 'ui/Modal';
import FormActions from 'ui/FormActions';
import { getApiErrorMessage } from "lib/errors/normalizeApiError";
import { getAuthUiStrings, resolveAuthLocale } from "i18n/authUi";

const PASSWORD_REQUIREMENTS = {
    minLength: 8,
    maxLength: 100
};

/**
 * Stable form id used to pair the password-reset form with its footer
 * Submit button so browser-native Enter-key submit fires the form's
 * `onSubmit`. See `docs/architecture/forms-standard.md` section 2.
 */
export const PASSWORD_RESET_FORM_ID = "auth-password-reset-form";

const PasswordResetPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token');
    const mode = searchParams.get('mode');
    const authUi = getAuthUiStrings(resolveAuthLocale());

    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);

    useEffect(() => {
        if (!token) {
            navigate('/');
        }
    }, [token, navigate]);

    const validatePassword = (password: string): string | null => {
        if (password.length < PASSWORD_REQUIREMENTS.minLength) {
            return authUi.passwordTooShort;
        }
        if (password.length > PASSWORD_REQUIREMENTS.maxLength) {
            return authUi.passwordTooLong;
        }
        return null;
    };

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e && typeof e.preventDefault === "function") {
            e.preventDefault();
        }
        setError(null);

        const passwordError = validatePassword(newPassword);
        if (passwordError) {
            setError(passwordError);
            return;
        }

        if (newPassword !== confirmPassword) {
            setError(authUi.passwordsDoNotMatch);
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await api.post(apiRoutes.auth.passwordReset, {
                token,
                newPassword
            });

            if (response.status === 200) {
                setIsSuccess(true);
                // Redirect to /signin after the user has had a chance to
                // read the success message.
                setTimeout(() => {
                    navigate('/signin');
                }, 3000);
            }
        } catch (err: unknown) {
            // Prefers backend response.data.message over axios's technical
            // "Request failed with status code N" default. See
            // `lib/errors/normalizeApiError`.
            setError(
                getApiErrorMessage(
                    err,
                    mode === "setup" ? authUi.passwordSetupTitle : authUi.passwordResetTitle
                )
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!token) {
        return null;
    }

    const isSubmitDisabled =
        isSubmitting ||
        !newPassword ||
        !confirmPassword ||
        newPassword !== confirmPassword ||
        newPassword.length < PASSWORD_REQUIREMENTS.minLength;

    return (
        <Modal
            isOpen={true}
            onClose={() => navigate('/signin')}
            title={mode === "setup" ? authUi.passwordSetupTitle : authUi.passwordResetTitle}
            formId={PASSWORD_RESET_FORM_ID}
            footer={
                isSuccess ? null : (
                    <FormActions
                        formId={PASSWORD_RESET_FORM_ID}
                        submitLabel={authUi.resetPasswordCta}
                        cancelLabel={authUi.cancelCta}
                        submittingLabel={authUi.submittingCta}
                        submitDisabled={isSubmitDisabled}
                        submitting={isSubmitting}
                        onCancel={() => navigate('/')}
                    />
                )
            }
        >
            <div className="flex flex-col flex-grow h-full justify-between mb-4 space-y-4">
                {isSuccess ? (
                    <div className="flex flex-col flex-grow justify-between mb-4 space-y-4">
                        <p className="text-green-600 dark:text-green-400">
                            {mode === "setup" ? authUi.passwordSetupSuccess : authUi.passwordResetSuccess}
                        </p>

                        <p className="text-eoe-text-meta dark:text-eoe-dark-text-meta">
                            {authUi.redirectingToSignIn}
                        </p>
                    </div>
                ) : (
                    <div className="flex flex-col w-full sm:h-auto overflow-y-scroll sm:overflow-y-auto">
                        <div className="flex flex-col flex-grow justify-between space-y-4 mb-4">
                            <p className="text-left text-sm text-eoe-text-meta dark:text-eoe-dark-text-meta">
                                {mode === "setup" ? authUi.passwordSetupDescription : authUi.passwordResetDescription}
                            </p>
                            <form
                                id={PASSWORD_RESET_FORM_ID}
                                onSubmit={handleSubmit}
                                className="space-y-4"
                                noValidate
                            >
                                <InputField
                                    type="password"
                                    id="newPassword"
                                    name="newPassword"
                                    labelText={authUi.passwordLabel}
                                    autoComplete="new-password"
                                    fieldRequired
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    validationState={
                                        newPassword.length >= PASSWORD_REQUIREMENTS.minLength
                                            ? 'valid'
                                            : newPassword
                                            ? 'invalid'
                                            : 'none'
                                    }
                                    spellCheck={false}
                                    maxLength={PASSWORD_REQUIREMENTS.maxLength}
                                    minLength={PASSWORD_REQUIREMENTS.minLength}
                                />

                                <InputField
                                    type="password"
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    labelText={authUi.passwordLabel}
                                    autoComplete="new-password"
                                    fieldRequired
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    validationState={
                                        confirmPassword === newPassword &&
                                        confirmPassword.length >= PASSWORD_REQUIREMENTS.minLength
                                            ? 'valid'
                                            : confirmPassword
                                            ? 'invalid'
                                            : 'none'
                                    }
                                    spellCheck={false}
                                />

                                {error && (
                                    <div className="flex items-center text-warning-red dark:text-warning-red-dark text-sm" role="alert">
                                        <Info className="mr-1 size-4" />
                                        <span>{error}</span>
                                    </div>
                                )}
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </Modal>
    );
};

export default PasswordResetPage;
